import { download, generateCsv, mkConfig } from "export-to-csv"
import { DateTime } from "luxon"
import { useCallback } from "react"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { MeetingServiceOrder } from "../accountingTypes"
import { toCurrency, toDinero } from "../../../../currency/currencyLib"
import { MonetaryAmount } from "../../../../currency/currencyTypes"
import { useDateTime } from "../../../../localization/useDateTime"
import { useMoney } from "../../../../currency/useMoney"
import { OrderLine } from "../../../controlKitchenTypes"
import { MeetingServicePaymentType, PaymentMethodType } from "../../../../payments/paymentTypes"
import { getOrderPerson, getOrganizers } from "../../../../../shared/utils/getOrderPerson"
import { DeliveryType } from "../../../merchant/production/meetingService/meetingServiceTypes"

type useExportOrdersToCsvParams = Readonly<{
    data: MeetingServiceOrder[] | null | undefined
    startDate: DateTime
    endDate: DateTime
    includeVatInLabels?: boolean
}>

export function useExportOrdersToCsv({
    data,
    startDate,
    endDate,
    includeVatInLabels = false,
}: useExportOrdersToCsvParams) {
    const dateTimeFactory = useDateTime()
    const moneyFactory = useMoney({ locale: "da-DK" })
    const { l10n } = useAppLocalization()
    const vatLabel = includeVatInLabels ? "includes VAT" : ""

    const csvOptions = mkConfig({
        fieldSeparator: ";",
        quoteStrings: true,
        decimalSeparator: ".",
        useBom: true,
        useKeysAsHeaders: true,
        filename: `Facilitynet-Orders-${dateTimeFactory.formatDate(startDate)}_${dateTimeFactory.formatDate(endDate)}`,
    })

    function getCurrencyString(amount: MonetaryAmount | undefined) {
        if (!amount) return ""
        const currency = toCurrency(amount.currency)
        return moneyFactory.getLocalizedCurrencySymbol(currency)
    }

    function getAmountString(amount: MonetaryAmount | undefined) {
        if (!amount) return ""
        const dinero = toDinero(amount)
        return moneyFactory.formatAsNumber(dinero)
    }

    function translatePaymentMethod(method?: PaymentMethodType | string) {
        if (!method) return ""
        return l10n.getStringForEnum("purchases-payment-method", method, method)
    }

    function translatePaymentType(type: MeetingServicePaymentType | undefined) {
        if (!type) return ""
        return l10n.getStringForEnum("accounting-orders-meeting-service-payment-type", type, type)
    }

    function translateDeliveryType(type: DeliveryType | string | undefined) {
        if (type === undefined) return ""
        return l10n.getStringForEnum("delivery-type-filter", type, type)
    }

    function formatDateTimeSafe(dateTimeString: string | undefined | null): string {
        if (!dateTimeString) return ""

        const dateTime = dateTimeFactory.fromApi(dateTimeString)
        return dateTime.isValid ? dateTimeFactory.formatDateTime(dateTime) : ""
    }

    const mapOrderToCsvRow = (
        order: MeetingServiceOrder | undefined,
        orderLine: NonNullable<MeetingServiceOrder["deliveries"]>[number]["orderLines"][number] | undefined,
    ) => {
        const orderPerson = order ? getOrderPerson(getOrganizers(order)) : undefined

        return {
            From: dateTimeFactory.formatDate(startDate),
            To: dateTimeFactory.formatDate(endDate),
            "Order Id": order?.id?.toString() ?? "",
            "Payment method": translatePaymentMethod(order?.paymentDetails?.method),
            Type: translatePaymentType(order?.paymentDetails?.type),
            "Booked date": order?.accountingDetails?.bookedTime
                ? dateTimeFactory.formatDate(dateTimeFactory.fromApi(order.accountingDetails.bookedTime))
                : "",
            "Delivery date": formatDateTimeSafe(order?.deliveryDetails?.time),
            "Product id": orderLine?.productId?.toString() ?? "",
            "Product name": orderLine?.name ?? "",
            Items: orderLine?.items?.toString() ?? "",
            Currency: getCurrencyString(orderLine?.itemPrice) || getCurrencyString(orderLine?.price) || "",
            ["Item price " + `(${vatLabel})`]: getAmountString(orderLine?.itemPrice),
            ["Amount " + `(${vatLabel})`]: getAmountString(orderLine?.price),
            "Order note": order?.orderNote ?? "",
            "Delivery type": translateDeliveryType(order?.deliveryDetails?.type),
            "Delivery location": order?.deliveryDetails?.location?.name ?? "",
            Sittings: order?.deliveryDetails?.sittings ?? "",
            "Meeting name": order?.meetingDetails?.meetingName ?? "",
            "Meeting host": orderPerson?.person?.name ?? "-",
            "Meeting start time":
                dateTimeFactory.formatDateTimeSafe(
                    dateTimeFactory.fromApiOrUndefined(order?.meetingDetails?.startDateTime),
                ) ?? "",
            "Meeting end time":
                dateTimeFactory.formatDateTimeSafe(
                    dateTimeFactory.fromApiOrUndefined(order?.meetingDetails?.endDateTime),
                ) ?? "",
            "Creator name": order?.customer?.displayName ?? "",
            "Creator email": order?.customer?.email ?? "",
            "Creator user group": order?.customer?.userGroup?.name ?? "",
            Company: order?.accountingCustomerParty?.name ?? "",
            "Sales Case Number": order?.sellerSupplierParty?.caseNumber ?? "",
            "Sales location": order?.sellerSupplierParty?.name ?? "",
            "Debtor no": order?.buyerCustomerParty?.debtorNo ?? order?.buyerCustomerParty?.debtorId ?? "",
            "Debtor name": order?.buyerCustomerParty?.name ?? "",
            "Debtor address": order?.buyerCustomerParty?.address?.displayName ?? "",
            "Debtor contact name": order?.buyerCustomerParty?.contactDetails?.name ?? "",
            "Debtor contact email": order?.buyerCustomerParty?.contactDetails?.email ?? "",
            "Debtor contact phone": order?.buyerCustomerParty?.contactDetails?.phoneNumber ?? "",
            "Debtor EAN": order?.buyerCustomerParty?.ean ?? "",
            "Cost center": order?.accountingDetails?.accountingCostCode ?? "",
            // TODO
            // "Debtor invoice email": '',
            // "Account Number": "",
            // "Participants": order.meetingDetails.attendees
        }
    }

    return useCallback(() => {
        const exportFallback = Object.keys(mapOrderToCsvRow(undefined, undefined)).reduce(
            (acc, key) => ({ ...acc, [key]: "" }),
            {},
        )
        const exportRows =
            data && data.length > 0
                ? data.flatMap((order) => {
                      const flatDeliveries = order.deliveries?.flatMap((delivery) => delivery.orderLines)
                      return flatDeliveries
                          ? flatDeliveries.map((orderLine) => mapOrderToCsvRow(order, orderLine))
                          : [exportFallback]
                  })
                : [exportFallback]

        const csv = generateCsv(csvOptions)(exportRows)
        download(csvOptions)(csv)
    }, [dateTimeFactory, moneyFactory, data, startDate, endDate, l10n, includeVatInLabels])
}
